import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
	assumedOrganizationSelector,
	assumedRoleSelector,
	roleSwitchDialogVisibleSelector,
	setAssumedOrganization,
	setAssumedRole,
	setRoleSwitchDialogVisible
} from '@store/slices/portal.slice';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { PORTAL_URL } from '@models';
import { Tr } from '@utils';
import { useLoggedinUser, useService } from '@hooks';
import { PredefinedRole, subOrganizationService, userService } from '@services';
import { Button, Chip, Grid, ListItemText, MenuItem, Stack } from '@mui/material';
import { VoltaageModalDialog, FormikTextfield } from '@components/common';

type FormFields = Extendable<{
	organizationId: string;
	roleId: string;
}>;

export interface IRoleSwitchDialogProps {}

export const RoleSwitchDialog: FC<IRoleSwitchDialogProps> = () => {
	const { data: clientAccounts = [] } = useService(() => subOrganizationService.getAllFromCache());
	const { data: organizations = [] } = useService(() => userService.doIfNotAdmin(userService.getSelectableRoles()), [clientAccounts]);
	const { t } = useTranslation('portal');
	const user = useLoggedinUser();
	const dispatch = useDispatch();
	const roleSwitchDialogVisible = useSelector(roleSwitchDialogVisibleSelector);
	const storedOrg = useSelector(assumedOrganizationSelector);
	const storedRole = useSelector(assumedRoleSelector);
	const getCurrentOrg = (org?: string) => organizations.find(({ organization: { id } }) => id === org);
	const getCurrentRole = (org?: string, role?: number) => getCurrentOrg(org)?.roles?.find(({ id }) => id === role);

	const formik = useFormik<FormFields>({
		initialValues: {
			organizationId: storedOrg?.id || user?.organization?.id || '',
			roleId: storedRole?.id || user?.role?.id || ''
		},
		enableReinitialize: true,
		validateOnChange: true,
		validationSchema: yup.object({
			organizationId: yup.string().required(t('field-required')),
			roleId: yup.string().required(t('field-required'))
		}),
		onSubmit: ({ organizationId, roleId }, api) => {
			dispatch(setAssumedOrganization(getCurrentOrg(organizationId)?.organization));
			dispatch(setAssumedRole(getCurrentOrg(organizationId)?.roles?.find(({ id }) => id === +roleId)));
			api.setSubmitting(false);
			dispatch(setRoleSwitchDialogVisible(false));
			setTimeout(() => (window.location.href = `/${PORTAL_URL}`), 500);
		}
	});
	const { handleSubmit } = formik;
	const disabled = !formik.isValid || formik.isSubmitting;

	return (
		<VoltaageModalDialog
			title={<Tr.Portal path="switch-role.title" />}
			dividers
			maxWidth="sm"
			open={roleSwitchDialogVisible}
			onClose={() => dispatch(setRoleSwitchDialogVisible(false))}
		>
			<Stack direction="column" alignItems="center" sx={{ padding: 3 }} spacing={3}>
				<form onSubmit={handleSubmit}>
					<Grid container rowSpacing={5}>
						<Grid item xs={12}>
							<FormikTextfield
								select
								formikApi={formik}
								name="organizationId"
								title={t('switch-role.organization')}
								placeholder={t('switch-role.organization')}
								required
								onChange={() => formik.setFieldValue('roleId', '')}
								SelectProps={{ renderValue: (val: any) => getCurrentOrg(val)?.organization.name || '' }}
							>
								{organizations.map(({ organization: { id, name } }) => {
									const isOriginal = id === user?.organization?.id;
									const isActive = (storedOrg && id === storedOrg.id) || (!storedOrg && isOriginal);
									return (
										<MenuItem key={`organization-${id}`} value={id}>
											<ListItemText>{name}</ListItemText>
											<Stack direction="row" spacing={1}>
												{isActive && (
													<Chip
														label={<Tr.Portal path="switch-role.active" />}
														color="primary"
														variant="filled"
														size="small"
													/>
												)}
												{isOriginal && (
													<Chip
														label={<Tr.Portal path="switch-role.original" />}
														color="secondary"
														variant="filled"
														size="small"
													/>
												)}
											</Stack>
										</MenuItem>
									);
								})}
							</FormikTextfield>
						</Grid>
						<Grid item xs={12}>
							<FormikTextfield
								select
								formikApi={formik}
								name="roleId"
								title={t('switch-role.role')}
								placeholder={t('switch-role.role')}
								required
								disabled={!formik.values.organizationId}
								SelectProps={{ renderValue: (v) => getCurrentRole(formik.values.organizationId, v as number)?.name || '' }}
							>
								{(getCurrentOrg(formik.values.organizationId)?.roles || [])
									.filter((role) => {
										const selectedOrg = getCurrentOrg(formik.values.organizationId);
										if (!selectedOrg) {
											return false;
										}

										if (selectedOrg.type === 'parent') {
											return (
												(user?.isInfraSeller() && role.name === PredefinedRole.CHARGING_PARTNER) ||
												(user?.isVehicleSeller() && role.name === PredefinedRole.DEALER)
											);
										}

										if (selectedOrg.type === 'child') {
											return !(
												(user?.isInfraSeller() && role.name === PredefinedRole.CHARGING_PARTNER) ||
												(user?.isVehicleSeller() && role.name === PredefinedRole.DEALER)
											);
										}

										return false;
									})
									.map(({ id, name }) => {
										const isOriginal = id === user?.role?.id;
										const isActive = (storedRole && id === storedRole.id) || (!storedRole && isOriginal);

										return (
											<MenuItem key={`role-${id}`} value={id}>
												<ListItemText>{name}</ListItemText>
												<Stack direction="row" spacing={1}>
													{isActive && (
														<Chip
															label={<Tr.Portal path="switch-role.active" />}
															color="primary"
															variant="filled"
															size="small"
														/>
													)}
													{isOriginal && (
														<Chip
															label={<Tr.Portal path="switch-role.original" />}
															color="secondary"
															variant="filled"
															size="small"
														/>
													)}
												</Stack>
											</MenuItem>
										);
									})}
							</FormikTextfield>
						</Grid>
						<Grid item xs={12} container>
							<Stack direction="row" alignItems="center" justifyContent="center" spacing={2} sx={{ width: 1.0 }}>
								<Button
									type="submit"
									variant="contained"
									color="secondary"
									disabled={disabled}
									data-testid="switch-role-submit-button"
								>
									<Tr.Portal path="switch-role.switch-role-button" />
								</Button>
								<Button
									variant="outlined"
									color="secondary"
									data-testid="switch-role-reset-role-button"
									disabled={
										storedOrg && storedRole
											? user?.organization?.id === storedOrg.id && user?.role?.id === storedRole.id
											: true
									}
									onClick={() => {
										dispatch(setAssumedOrganization(null));
										dispatch(setAssumedRole(null));
										dispatch(setRoleSwitchDialogVisible(false));
										setTimeout(() => (window.location.href = `/${PORTAL_URL}`), 500);
									}}
								>
									<Tr.Portal path="switch-role.reset-role-button" />
								</Button>
							</Stack>
						</Grid>
					</Grid>
				</form>
			</Stack>
		</VoltaageModalDialog>
	);
};
